/**
 * Shared fetch helper for the three Cloud Function clients.
 * - Reads VITE_FUNCTIONS_BASE.
 * - Retries once on 5xx OR 429 (rate-limited) with 500ms backoff.
 * - Default 15s timeout via AbortSignal.timeout (composed with caller signal).
 * - Throws on 4xx (except 429) — callers handle the message.
 *
 * Network resilience hardened by the Task 4 code-quality review.
 */
import { log } from '../log.js';

const BASE = import.meta.env.VITE_FUNCTIONS_BASE;
if (!BASE) {
  log.error('VITE_FUNCTIONS_BASE is unset — Cloud Function calls will fail');
}

const DEFAULT_TIMEOUT_MS = 15_000;

export interface PostOptions {
  signal?: AbortSignal;
  retryOn5xx?: boolean; // default true
  timeoutMs?: number;   // default 15_000
}

export async function postJson<TIn, TOut>(
  path: string,
  body: TIn,
  opts: PostOptions = {},
): Promise<TOut> {
  return doPost(path, JSON.stringify(body), { 'Content-Type': 'application/json' }, opts);
}

export async function postFormData<TOut>(
  path: string,
  form: FormData,
  opts: PostOptions = {},
): Promise<TOut> {
  return doPost(path, form, undefined, opts);
}

/** Compose caller signal with a default timeout signal so we always have one. */
function buildSignal(opts: PostOptions): AbortSignal {
  const timeout = AbortSignal.timeout(opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  if (!opts.signal) return timeout;
  // AbortSignal.any composes — first to abort wins. Falls back to caller signal
  // if `any` is unavailable in the runtime (very old browsers).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyFn = (AbortSignal as unknown as { any?: (sigs: AbortSignal[]) => AbortSignal }).any;
  return anyFn ? anyFn([opts.signal, timeout]) : opts.signal;
}

async function doPost<TOut>(
  path: string,
  body: BodyInit,
  headers: HeadersInit | undefined,
  opts: PostOptions,
): Promise<TOut> {
  const url = `${BASE}${path}`;
  const init: RequestInit = { method: 'POST', body, signal: buildSignal(opts) };
  if (headers) init.headers = headers;
  const retry = opts.retryOn5xx !== false;

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    log.warn('postJson network error', err);
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`POST ${path} network error: ${msg}`, { cause: err });
  }
  if (res.ok) return res.json() as Promise<TOut>;

  // Retry on 5xx and 429 (rate-limited). Pl@ntNet rate-limits aggressively
  // and a transient backoff shouldn't surface as a hard error.
  const isRetryable = res.status >= 500 || res.status === 429;
  if (isRetryable && retry) {
    await new Promise(r => setTimeout(r, 500));
    let res2: Response;
    try {
      res2 = await fetch(url, init);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new Error(`POST ${path} network error on retry: ${msg}`, { cause: err });
    }
    if (res2.ok) return res2.json() as Promise<TOut>;
    throw new Error(`POST ${path} failed after retry: ${res2.status}`);
  }
  const text = await res.text().catch(() => '');
  throw new Error(`POST ${path} failed: ${res.status} ${text}`);
}
